import React, { forwardRef, useMemo } from "react";
import { useSelector } from "react-redux";
import TextField from "@mui/material/TextField";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { getDefaultCountryCode } from "./utils";
import "react-phone-number-input/style.css";
import "./PhoneNumberInput.css";

/**
 * MUI TextField adapter for react-phone-number-input.
 * Must forwardRef to the underlying input element.
 */
const MuiPhoneTextField = forwardRef(function MuiPhoneTextField(props, ref) {
  const {
    label,
    error,
    helperText,
    variant = "standard",
    fullWidth = true,
    disabled,
    required,
    name,
    id,
    autoComplete,
    sx,
    className,
    ...rest
  } = props;

  return (
    <TextField
      {...rest}
      className={className}
      id={id}
      name={name}
      label={label}
      error={Boolean(error)}
      helperText={helperText}
      variant={variant}
      fullWidth={fullWidth}
      disabled={disabled}
      required={required}
      autoComplete={autoComplete}
      sx={sx}
      inputRef={ref}
    />
  );
});

/**
 * Shared phone input with country select.
 * Value is E.164 (e.g. "+919876543210") for use with parsePhoneNumber / OTP APIs.
 */
const PhoneNumberInput = forwardRef(function PhoneNumberInput(
  {
    value,
    onChange,
    onBlur,
    defaultCountry: defaultCountryProp,
    countries,
    label = "Mobile",
    error = false,
    helperText,
    variant = "standard",
    fullWidth = true,
    disabled = false,
    required = false,
    name,
    id,
    autoComplete = "tel",
    className,
    sx,
    ...rest
  },
  ref
) {
  const { appConfigs } = useSelector((state) => state.auth);
  const defaultCountry = useMemo(
    () => defaultCountryProp ?? getDefaultCountryCode(appConfigs),
    [defaultCountryProp, appConfigs]
  );

  return (
    <PhoneInput
      key={value ? "filled" : defaultCountry}
      {...rest}
      international
      countryCallingCodeEditable={!disabled}
      defaultCountry={defaultCountry}
      countries={countries}
      value={value || undefined}
      onChange={(next) => onChange?.(next || "")}
      onBlur={onBlur}
      disabled={disabled}
      readOnly={disabled}
      inputRef={ref}
      countrySelectProps={{
        disabled,
        readOnly: disabled,
        ...(rest.countrySelectProps || {}),
      }}
      className={[
        "PhoneNumberInput",
        `PhoneNumberInput--${variant}`,
        error ? "PhoneNumberInput--error" : "",
        disabled ? "PhoneNumberInput--disabled" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      inputComponent={MuiPhoneTextField}
      label={label}
      error={error}
      helperText={helperText}
      variant={variant}
      fullWidth={fullWidth}
      required={required}
      name={name}
      id={id}
      autoComplete={autoComplete}
      sx={sx}
    />
  );
});

export { isValidPhoneNumber };
export default PhoneNumberInput;
